/**
 * Composable for fetching block text content from a CMS.
 * Retrieves localized text based on a delivery key and current route context.
 *
 * @param {string} deliveryKey - Unique identifier for the block text content
 * @returns {Promise<{data: Ref<any>}>} An object containing the fetched block text data
 *
 * @example
 * const { data } = await useBlockText('welcome_message');
 * console.log(data.value); // Displays the block text content
 */
export const useBlockText = async (deliveryKey: string) => {
  // Get the current localized path
  const localePath = useLocalePath()

  /**
   * Query parameters for fetching block text.
   * Includes locale, VSE (Virtual Staging Environment), content ID, and delivery key.
   *
   * @type {Object}
   * @property {string} locale - Current localized path
   * @property {string} [vse] - Virtual Staging Environment from route query
   * @property {string} [contentId] - Content ID from route query
   * @property {string} deliveryKey - Unique identifier for the block text
   */
  const query = {
    locale: localePath(''),
    vse: useRoute().query.vse,
    contentId: useRoute().query.contentId,
    deliveryKey: deliveryKey,
  }

  // Fetch block text using the CMS API
  const { data } = await useFetch('/api/cms/getBlockText', {
    query,
  })

  return {
    data,
  }
}
